<script setup lang="ts">
import { AppButton, AppTypography } from '@/components';
import { useConfirmDialog } from '@/composables/useConfirmDialog';

const { isOpen, options, handleConfirm, handleCancel, pending } = useConfirmDialog();

const modal = defineModel<boolean>({
  default: false,
});

watch(isOpen, (newValue) => {
  modal.value = newValue;
});

watch(modal, (newValue) => {
  isOpen.value = newValue;
});
</script>

<template>
  <v-dialog v-model="modal" width="295" :close-on-content-click="false" :persistent="options?.persistent">
    <v-card class="app-confirm-dialog" width="100%" max-width="295">
      <div class="d-flex flex-column align-center pt-8 pb-6 px-4">
        <v-card-title class="d-flex flex-column align-center py-0">
          <template v-if="options?.icon">
            <slot name="icon">
              <div class="mt-6 mb-3">
                <div class="p-1"><img :src="options?.icon" :alt="options?.icon" class="w-8" /></div>
                <!-- <v-icon size="40px" :icon="options?.icon" :color="options?.iconColor" rounded="true" flat> </v-icon> -->
              </div>
            </slot>
          </template>
          <slot name="title">
            <AppTypography element="h3" type="title-3" class="text-wrap text-center">
              {{ options?.title }}
            </AppTypography>
          </slot>
        </v-card-title>
        <v-card-text class="text-center pt-2 pb-0 px-0">
          <slot name="description">
            <AppTypography element="span" type="subheadline" color="-tertiary">
              {{ options?.description }}
            </AppTypography>
          </slot>
          <slot name="list-description">
            <ul v-if="options?.listDescription?.length" class="app-confirm-dialog__list-description">
              <li v-for="(item, index) in options?.listDescription" :key="index">
                <AppTypography element="span" type="subheadline" color="-tertiary">
                  {{ item }}
                </AppTypography>
              </li>
            </ul>
          </slot>
        </v-card-text>
      </div>
      <v-card-actions v-if="!options?.alert" class="pa-0">
        <div class="d-flex flex-column w-100">
          <AppButton
            data-cy="confirm-dialog-confirm-btn"
            rounded="none"
            block
            color="primary"
            variant="flat"
            data-confirm
            @click="handleConfirm"
            :loading="pending"
            :disabled="pending"
          >
            <AppTypography
              type="body-1"
              :color="
                options?.confirmTextColor as
                  | 'primary'
                  | 'secondary'
                  | 'tertiary'
                  | 'fields'
                  | 'border'
                  | 'on-tertiary'
                  | 'error'
                  | 'black'
                  | 'background'
              "
            >
              {{ options?.confirmText }}
            </AppTypography>
          </AppButton>
          <AppButton
            v-if="options?.hasCancel"
            data-cy="confirm-dialog-cancel-btn"
            rounded="none"
            block
            color="primary"
            variant="flat"
            data-cancel
            @click="handleCancel"
            :disabled="pending"
          >
            {{ options?.cancelText }}
          </AppButton>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
@use '../../assets/scss/_fonts.scss';

.v-card {
  overflow: hidden !important;
  background-color: rgb(var(--v-theme-surface)) !important;
}

.v-card-title {
  padding: 0;
}

.rounded-none {
  border-radius: 0 !important;
}

.v-card-actions {
  button {
    width: 100%;
    background-color: transparent !important;
    border-top: 1px solid rgb(var(--v-theme-stroke)) !important;
    height: 56px !important;

    &[data-confirm] {
      @extend .font-body-1;
    }

    &[data-cancel] {
      @extend .font-body-2;
    }
  }
}

.app-confirm-dialog {
  &__list-description {
    margin-top: 12px;
    padding-left: 24px;
    text-align: left;

    li {
      margin-bottom: 8px;

      &::marker {
        color: rgb(var(--v-theme--tertiary));
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
